
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { ArrowLeft, Mail, Users, Calendar, CheckCircle, XCircle, Clock, AlertCircle, FileText } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

interface BulkEmailDetail {
  id: string;
  subject: string;
  body: string;
  status: string;
  recipient_count: number;
  sent_at: string | null;
  created_at: string;
  error_message?: string;
  template_id?: string;
  email_templates?: {
    name: string;
  };
}

const EmailHistoryDetail = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [email, setEmail] = useState<BulkEmailDetail | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!id) {
      toast({
        title: "Error",
        description: "Email record not found.",
        variant: "destructive",
      });
      navigate('/email-center');
      return;
    }

    fetchEmailDetail();
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps -- fetchEmailDetail depends on id

  const fetchEmailDetail = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('bulk_emails')
        .select(`
          id,
          subject,
          body,
          status,
          recipient_count,
          sent_at,
          created_at,
          error_message,
          template_id,
          email_templates (
            name
          )
        `)
        .eq('id', id)
        .single();

      if (error) throw error;
      
      if (!data) {
        toast({
          title: "Error",
          description: "Email record not found.",
          variant: "destructive",
        });
        navigate('/email-center');
        return;
      }

      setEmail(data);
    } catch (error) {
      console.error('Error fetching email detail:', error);
      toast({
        title: "Error",
        description: "Failed to fetch email details. Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'sent':
      case 'completed':
        return <CheckCircle className="w-4 h-4 text-green-600" />;
      case 'failed':
      case 'error':
        return <XCircle className="w-4 h-4 text-red-600" />;
      case 'sending':
      case 'processing':
        return <Clock className="w-4 h-4 text-blue-600" />;
      case 'pending':
        return <Clock className="w-4 h-4 text-yellow-600" />;
      default:
        return <AlertCircle className="w-4 h-4 text-gray-600" />;
    }
  };

  const getStatusBadge = (status: string) => {
    const variants = {
      sent: 'bg-green-100 text-green-800 border-green-200',
      completed: 'bg-green-100 text-green-800 border-green-200',
      failed: 'bg-red-100 text-red-800 border-red-200',
      error: 'bg-red-100 text-red-800 border-red-200',
      sending: 'bg-blue-100 text-blue-800 border-blue-200',
      processing: 'bg-blue-100 text-blue-800 border-blue-200',
      pending: 'bg-yellow-100 text-yellow-800 border-yellow-200',
    };
    return (
      <Badge variant="outline" className={`capitalize font-medium ${variants[status] || 'bg-gray-100 text-gray-800 border-gray-200'}`}>
        {status}
      </Badge>
    );
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'Not sent yet';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="p-6 max-w-7xl mx-auto">
          <div className="mb-6">
            <Button variant="ghost" onClick={() => navigate('/email-center')} className="mb-4">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Email Center
            </Button>
            <Skeleton className="h-8 w-64 mb-2" />
            <Skeleton className="h-6 w-96" />
          </div>

          <div className="space-y-6">
            <Card>
              <CardHeader>
                <Skeleton className="h-6 w-48" />
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <Skeleton className="h-4 w-full" />
                  <Skeleton className="h-4 w-3/4" />
                  <Skeleton className="h-4 w-1/2" />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <Skeleton className="h-6 w-32" />
              </CardHeader>
              <CardContent>
                <Skeleton className="h-32 w-full" />
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    );
  }

  if (!email) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="p-6 max-w-7xl mx-auto">
          <Button variant="ghost" onClick={() => navigate('/email-center')} className="mb-4">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Email Center
          </Button>
          <Card className="border-2 border-dashed border-gray-200 bg-gray-50">
            <CardContent className="text-center py-12">
              <div className="p-4 bg-white rounded-full w-fit mx-auto mb-4">
                <Mail className="w-8 h-8 text-gray-400" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Email not found</h3>
              <p className="text-gray-600">
                The email you're looking for doesn't exist or has been deleted.
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="p-6 max-w-7xl mx-auto">
        <div className="mb-8">
          <Button variant="ghost" onClick={() => navigate('/email-center')} className="mb-4">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Email Center
          </Button>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Email Details</h1>
          <p className="text-gray-600">View detailed information about this bulk email</p>
        </div>

        <div className="space-y-6">
          {/* Email Overview */}
          <Card className="border border-gray-200 shadow-sm">
            <CardHeader className="pb-4">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="p-2 bg-gray-100 rounded-lg">
                      <Mail className="w-4 h-4 text-gray-600" />
                    </div>
                    {getStatusIcon(email.status)}
                    <CardTitle className="text-xl text-gray-900">{email.subject}</CardTitle>
                    {getStatusBadge(email.status)}
                  </div>
                  
                  <div className="flex items-center gap-6 text-sm text-gray-600">
                    <div className="flex items-center gap-2 bg-blue-50 px-3 py-1 rounded-full">
                      <Users className="w-4 h-4 text-blue-600" />
                      <span className="font-medium text-blue-800">{email.recipient_count} recipients</span>
                    </div>
                    
                    <div className="flex items-center gap-2 bg-gray-50 px-3 py-1 rounded-full">
                      <Calendar className="w-4 h-4 text-gray-600" />
                      <span className="font-medium text-gray-700">{formatDate(email.sent_at)}</span>
                    </div>
                    
                    {email.email_templates?.name && (
                      <div className="flex items-center gap-2 bg-purple-50 px-3 py-1 rounded-full">
                        <FileText className="w-4 h-4 text-purple-600" />
                        <span className="font-medium text-purple-800">{email.email_templates.name}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </CardHeader>
          </Card>

          {/* Error Message */}
          {email.error_message && (
            <Card className="border-red-200 bg-red-50">
              <CardContent className="pt-6">
                <div className="flex items-center gap-2">
                  <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold text-red-800 mb-1">Error Details</h3>
                    <p className="text-sm text-red-700">{email.error_message}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Email Content */}
          <Card className="border border-gray-200 shadow-sm">
            <CardHeader>
              <CardTitle className="text-lg text-gray-900">Email Content</CardTitle>
              <CardDescription>The content that was sent to recipients</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                <div 
                  className="prose prose-sm max-w-none"
                  dangerouslySetInnerHTML={{ __html: email.body }}
                />
              </div>
            </CardContent>
          </Card>

          {/* Metadata */}
          <Card className="border border-gray-200 shadow-sm">
            <CardHeader>
              <CardTitle className="text-lg text-gray-900">Email Metadata</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                <div>
                  <p className="text-sm font-medium text-gray-600">Email ID</p>
                  <p className="text-sm text-gray-900 font-mono">{email.id}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-600">Created At</p>
                  <p className="text-sm text-gray-900">{formatDate(email.created_at)}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-600">Status</p>
                  <p className="text-sm text-gray-900 capitalize">{email.status}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-600">Recipients</p>
                  <p className="text-sm text-gray-900">{email.recipient_count}</p>
                </div>
                {email.sent_at && (
                  <div>
                    <p className="text-sm font-medium text-gray-600">Sent At</p>
                    <p className="text-sm text-gray-900">{formatDate(email.sent_at)}</p>
                  </div>
                )}
                {email.template_id && (
                  <div>
                    <p className="text-sm font-medium text-gray-600">Template</p>
                    <p className="text-sm text-gray-900">{email.email_templates?.name || 'Template Used'}</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default EmailHistoryDetail;
